import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Bookmark, BookmarkCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { lt } from "../localize";
import type { LessonStage } from "../types";

interface StageTakeawayProps {
  stage: LessonStage;
}

export const StageTakeaway = ({ stage }: StageTakeawayProps) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const takeaway = stage.takeaway;
  const [saved, setSaved] = useState(false);
  const [flipped, setFlipped] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setFlipped(true), 500);
    return () => clearTimeout(timer);
  }, []);

  if (!takeaway) return null;

  const handleSave = () => {
    // Save to localStorage deck
    const deck = JSON.parse(localStorage.getItem("takeaway-deck") || "[]") as string[];
    const text = lt(takeaway.text, lang);
    if (!deck.includes(text)) {
      deck.push(text);
      localStorage.setItem("takeaway-deck", JSON.stringify(deck));
    }

    // Trigger fly-to-corner animation
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      setSaved(true);
    }, 800);
  };

  return (
    <div className="flex items-center justify-center min-h-[300px]">
      <div className="w-full max-w-md" style={{ perspective: "800px" }}>
        <div
          className={cn(
            "relative w-full transition-transform duration-700 ease-out",
            saving && "animate-[flyToCorner_0.8s_ease-out]",
          )}
          style={{
            transformStyle: "preserve-3d",
            transform: flipped ? "rotateY(0deg)" : "rotateY(180deg)",
          }}
        >
          {/* Front face — the golden takeaway card */}
          <div
            className="relative p-6 rounded-xl border-2 border-secondary/60 bg-gradient-to-br from-secondary/10 to-secondary/5 backdrop-blur-sm"
            style={{ backfaceVisibility: "hidden" }}
          >
            {/* Save button */}
            <button
              onClick={handleSave}
              disabled={saved}
              className={cn(
                "absolute top-3 right-3 transition-all duration-300",
                saved
                  ? "text-primary"
                  : "text-secondary hover:text-secondary/80 hover:scale-110",
              )}
              title={saved ? t("learn_saved", "Saved") : t("learn_save", "Save")}
            >
              {saved ? (
                <BookmarkCheck className="w-5 h-5" />
              ) : (
                <Bookmark className="w-5 h-5" />
              )}
            </button>

            {/* Star icon */}
            <div className="flex justify-center mb-3">
              <span className="text-2xl" role="img" aria-label="star">
                &#11088;
              </span>
            </div>

            {/* Takeaway text */}
            <p className="text-lg font-bold leading-relaxed pr-8 text-center">
              {lt(takeaway.text, lang)}
            </p>

            {/* Decorative bottom */}
            <div className="mt-4 flex flex-col items-center gap-1.5">
              <div className="w-12 h-px bg-secondary/40" />
              <span className="text-[10px] font-semibold uppercase tracking-widest text-secondary/60">
                Key Takeaway
              </span>
            </div>
          </div>

          {/* Back face — dark glass with spade watermark */}
          <div
            className="absolute inset-0 flex flex-col items-center justify-center rounded-xl border-2 border-white/10 bg-white/[0.04] backdrop-blur-sm"
            style={{
              backfaceVisibility: "hidden",
              transform: "rotateY(180deg)",
            }}
          >
            <span className="text-7xl text-white/[0.06] select-none">
              &#9824;
            </span>
            <span className="mt-4 text-xs font-medium text-white/20 tracking-wider uppercase">
              SmartPoker Academy
            </span>
          </div>
        </div>

        <p className="text-xs text-muted-foreground text-center mt-3">
          {t("learn_takeaway_hint", "Remember this at the table")}
        </p>
      </div>
    </div>
  );
};
